import { type Status, formatPeriodLong } from '@commissionsight/shared';

/** Tailwind classes for a status pill (light + dark mode via semantic tokens). */
export function statusClasses(status: Status): string {
  switch (status) {
    case 'green':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300';
    case 'yellow':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
    default:
      return 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300';
  }
}

export function statusDotClasses(status: Status): string {
  switch (status) {
    case 'green':
      return 'bg-emerald-500';
    case 'yellow':
      return 'bg-amber-500';
    default:
      return 'bg-rose-500';
  }
}

const FLAG_LABELS: Record<string, string> = {
  NEW: 'New',
  COMMISSION_CHANGED: 'Commission Δ',
  DATA_CHANGED: 'Data Δ',
  DROPPED: 'Dropped',
  REAPPEARED: 'Reappeared',
  REAPPEARED_WITH_DELTA: 'Reappeared Δ',
  CHARGEBACK: 'Chargeback',
  TERMINATING: 'Terminating',
  TERMINATED: 'Terminated',
};

export function flagLabel(flag: string): string {
  return FLAG_LABELS[flag] ?? flag;
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

export interface ExplainInput {
  status: Status;
  flags: string[];
  commissionAmount: number | null;
  prevCommissionAmount: number | null;
  comparedAgainstPeriod: string | null;
}

/** Plain-English "why this status" for a member — the explainability layer. */
export function explainRow(r: ExplainInput): string {
  const f = new Set(r.flags);
  const against = r.comparedAgainstPeriod
    ? formatPeriodLong(r.comparedAgainstPeriod)
    : 'the prior period';
  const delta = (r.commissionAmount ?? 0) - (r.prevCommissionAmount ?? 0);
  const dir = delta >= 0 ? 'up' : 'down';
  const absDelta = formatCurrency(Math.abs(delta));

  if (f.has('DROPPED')) {
    return `On the ${against} statement (${formatCurrency(r.prevCommissionAmount)}) but absent now — counted as fallout.`;
  }
  if (f.has('NEW')) {
    return `First time on the book — new member at ${formatCurrency(r.commissionAmount)}.`;
  }
  if (f.has('REAPPEARED')) {
    const base = `Returned after dropping off (last seen ${against})`;
    return f.has('REAPPEARED_WITH_DELTA')
      ? `${base}; commission ${dir} ${absDelta} vs last seen.`
      : `${base} at the same commission.`;
  }
  if (f.has('COMMISSION_CHANGED')) {
    return `Commission ${dir} ${absDelta} vs ${against} (${formatCurrency(r.prevCommissionAmount)} → ${formatCurrency(r.commissionAmount)}).`;
  }
  if (f.has('DATA_CHANGED')) {
    return `A tracked detail (plan, contact, etc.) changed vs ${against}.`;
  }
  return `Continuing — unchanged vs ${against}.`;
}
